import { z } from 'zod'

/**
 * Zod schemas mirroring `Budget/Models/BudgetModels.swift`.
 *
 * Until WEB-63 ships codegen Zod → Swift, drift is the highest risk.
 * Keep these in lockstep when iOS changes — same field names, same
 * optionality, same enums. The cross-platform parity ledger
 * (docs/plans/PLATFORM-PARITY.md) tracks the contract.
 *
 * Note on shape: Firestore stores numbers as `number` — we don't have
 * iOS's `Double` precision concerns for the values we care about.
 * Money is stored in dollars (not cents) for parity with iOS;
 * IEEE-754 rounding is acceptable at our scale and matches the iOS
 * implementation. Switching to cents-as-int is a future migration.
 */

// YNAB-style goal types. `null` for categories with no goal set.
// NEED = "Needed for Spending" (recurring), MF = "Monthly Funding"
// (save until target), DEBT = debt payoff, TB = "Targeted Balance"
// (one-time target). Mirrors YNAB's goal_type enum.
export const GoalTypeSchema = z.enum(['NEED', 'MF', 'DEBT', 'TB'])
export type GoalType = z.infer<typeof GoalTypeSchema>

// Cadence for NEED-style recurring goals. Maps to YNAB's
// goal_cadence: 0=none (one-time), 1=monthly, 2=weekly, 13=yearly,
// 3-12 = "every N months". We expose just the common three the UI
// supports today.
export const GoalCadenceSchema = z.enum(['monthly', 'weekly', 'yearly'])
export type GoalCadence = z.infer<typeof GoalCadenceSchema>

export const BudgetCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  groupId: z.string().uuid(),
  sortOrder: z.number().int().default(0),
  isHidden: z.boolean().default(false),
  assigned: z.number().default(0),
  activity: z.number().default(0),
  available: z.number().default(0),
  carryOver: z.number().default(0),
  goalId: z.string().uuid().nullable().optional(),
  // Inline goal data. We don't model goals as a separate collection
  // — the quick-fund UI is purely category-local.
  goalType: GoalTypeSchema.nullable().optional(),
  // Total target amount (full goal in dollars). NEED/MF: target each
  // cadence period. TB/DEBT: total amount to accumulate / pay off.
  target: z.number().nullable().optional(),
  // NEED-only: how often we re-need the full target. Optional even
  // when goalType=NEED so YNAB legacy data round-trips.
  goalCadence: GoalCadenceSchema.nullable().optional(),
  // NEED-only: day-of-month (1-31) the target should be ready by.
  goalDay: z.number().int().min(1).max(31).nullable().optional(),
  // NEED yearly-only: which month (1-12) the full target is due,
  // recurring every year — deliberately year-less.
  goalMonth: z.number().int().min(1).max(12).nullable().optional(),
  // MF-only: how much to set aside each month toward `target`.
  goalMonthlyContribution: z.number().nullable().optional(),
  // TB/DEBT/NEED-by-date: optional target month, ISO 'YYYY-MM'.
  targetMonth: z.string().nullable().optional(),
  // Amount still needed THIS MONTH to stay on track. Mirrors YNAB's
  // `goal_under_funded` — already pro-rated for the cadence.
  targetUnderfunded: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  isCreditCardPayment: z.boolean().default(false),
})
export type BudgetCategory = z.infer<typeof BudgetCategorySchema>

export const CategoryGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int().default(0),
  isHidden: z.boolean().default(false),
  categories: z.array(BudgetCategorySchema).default([]),
})
export type CategoryGroup = z.infer<typeof CategoryGroupSchema>

export const BudgetMonthSchema = z.object({
  id: z.string().uuid(),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  categoryGroups: z.array(CategoryGroupSchema).default([]),
  income: z.number().default(0),
  budgeted: z.number().default(0),
  activity: z.number().default(0),
  available: z.number().default(0),
  // Ready-to-assign carried in from prior months (YNAB: last month's
  // to_be_budgeted). TBA = carryOver + income - assigned; without it
  // TBA would be month-local and disagree with the bank.
  carryOver: z.number().default(0),
  ageOfMoney: z.number().int().nullable().optional(),
})
export type BudgetMonth = z.infer<typeof BudgetMonthSchema>
